// @ts-check

/**
 * @typedef dataEntry
 * @property {number} id - Unique identifier for the filter option
 * @property {string} title - Display name for the filter option
 * @property {?string} uri - Optional URI for the filter option
 */

/**
 * @typedef filter
 * @property {Array<dataEntry>} data - Array of available filter options
 * @property {string} dataKey - Property name in entry data that contains filterable values (e.g., 'postCategories')
 * @property {string} elementId - HTML element ID for the filter UI component
 * @property {string} filterKey - Internal identifier for this filter (e.g., 'category')
 * @property {string} urlKey - URL parameter name for this filter
 * @property {boolean} [multipleSelect] - Whether multiple values can be selected
 * @property {Array<number>} [selected] - Currently selected filter values (added at runtime)
 * @property {HTMLElement} [el] - DOM element reference (added at runtime)
 */

/**
 * @typedef config
 * @property {Array<filter>} filters - Array of filter configurations
 * @property {Array<string>} [searchKeys] - Array of entry properties to search in (e.g., ['title', 'summary'])
 * @property {number} [perPage] - Number of entries per page
 */

import { split } from "lodash-es";
import { debounce } from "throttle-debounce";

/**
 * Creates a filtering and pagination system for entries.
 *
 * Key concepts:
 * - dataKey: The property name in each entry object that contains the filterable data
 * - filterKey: The internal identifier used to manage this filter's state
 *
 * Example usage:
 * ```javascript
 * filterEntries(entriesJson, {
 *   filters: [{
 *     data: categoriesJson,
 *     dataKey: 'postCategories',  // entry.postCategories contains the data
 *     filterKey: 'category',      // internal identifier for this filter
 *     elementId: 'postCategories',
 *     urlKey: 'category',
 *     multipleSelect: true
 *   }]
 * })
 * ```
 *
 * @param {string} entries - JSON string of entries to filter
 * @param {config} config - Filter configuration object
 * @returns {object} Alpine.js component object with filtering functionality
 */
export default function filterEntries(entries = "", config = {
  filters: [],
  searchKeys: []
}) {
  return {
    /** @type {config} Configuration object containing filter definitions */
    config: { filters: [], searchKeys: [], ...config },

    get filters() {
      return this.config.filters;
    },

    get searchKeys() {
      return this.config.searchKeys || [];
    },

    get selectedCount() {
      return this.filters.map((filter) => filter.selected).flat().length;
    },

    /** @type {object[]} Dataset to filter */
    entries: [],

    /** @type {string} The current search query */
    searchQuery: "",

    /** @type {string} The debounced search query used for filtering */
    debouncedSearchQuery: "",

    /** @type {number} The current page number. */
    page: 1, // Current page

    /** @type {number} The number of entries to display per page. */
    perPage: config.perPage ?? 12, // Number of entries per page

    /** @type {string} The current sort order. */
    sortBy: "asc",

    /** @type {boolean} Indicates whether data is currently being loaded. */
    loading: false, // true when loading data

    /**
     * Initialize the filter system
     * - Validates configuration
     * - Sets up filter data and DOM references
     * - Processes URL parameters for initial state
     * - Sets up reactive watchers
     */
    init() {
      try {
        this.validateConfig(config);
      } catch (error) {
        console.error(error);
        return;
      }

      this.config = config;

      this.filters.forEach((filter) => {
        filter.data = JSON.parse(filter.data);
        filter.el = this.$refs[filter.elementId] ?? null;
      });

      this.processRequest();
      this.preventSelectionWhileLoading();

      this.entries = JSON.parse(entries);

      this.$watch("sortBy", () => {
        this.handleSortChange();
      });

      this.$watch("page", () => {
        this.replaceHistoryState();
      });

      // Initialize search functionality
      if (this.searchKeys.length > 0) {
        // Create debounced search handler
        this.debouncedSearch = debounce(300, () => {
          this.debouncedSearchQuery = this.searchQuery;
          this.page = 1;
          this.replaceHistoryState();
        });

        // Watch for search query changes
        this.$watch("searchQuery", () => {
          this.loading = true;
          this.debouncedSearch();
        });

        this.$watch("debouncedSearchQuery", () => {
          this.loading = false;
        });

        // Check for search query in URL
        const url = new URL(window.location.toString());
        const searchParam = url.searchParams.get("search");
        if (searchParam) {
          this.searchQuery = searchParam;
          this.debouncedSearchQuery = searchParam;
        }
      }
    },

    /**
     * validateConfig
     */
    validateConfig(config) {
      if (!config) {
        throw new Error("Config object is required.");
      }

      if (config instanceof Object === false) {
        throw new Error("Config must be an object.");
      }

      if (!config.filters) {
        throw new Error("config.filters is required.");
      }

      if (config.filters instanceof Array === false) {
        throw new Error("config.filters must be an array.");
      }
    },

    /**
     * Get a filter configuration by its filterKey (internal identifier)
     * @param {string} filterKey - The internal filter identifier (e.g., 'category')
     * @returns {filter|undefined} The filter configuration object
     */
    getFilter(filterKey) {
      return this.filters.find((filter) => filter.filterKey === filterKey);
    },

    /**
     * Get a filter data entry by its ID
     * Used to display filter option details (e.g., in filter chips)
     * @param {number} id - The data entry ID to find
     * @returns {dataEntry|undefined} The data entry object with title, etc.
     */
    getFilterDataEntryByDataEntryId(id) {
      return this.filters
        .map((filter) => filter.data)
        .flat()
        .find((data) => data.id === id);
    },

    /**
     * Process URL parameters to set initial filter state
     * Maps URL parameters (urlKey) to filter state (filterKey)
     * Example: URL param 'category=1,2' sets filter 'category' to [1,2]
     */
    processRequest() {
      const url = new URL(window.location.toString());

      const parse = (value) => {
        return isNaN(value) ? value : +value;
      };

      this.filters.forEach((filter) => {
        let uKey = filter.urlKey;
        let fKey = filter.filterKey;
        this.setFilterSelected(
          fKey,
          url.searchParams.get(uKey)
            ? split(url.searchParams.get(uKey), ",").map(v => v.trim()).map(parse)
            : []
        );
      });

      const pageParam = url.searchParams.get("page");
      this.page = pageParam ? +pageParam : 1;
    },

    /**
     * Set selected values for a filter
     * @param {string} filterKey - The internal filter identifier
     * @param {Array<number>} value - Array of selected data entry IDs
     */
    setFilterSelected(filterKey, value) {
      const filter = this.getFilter(filterKey);

      if (filter) {
        filter.selected = value;
      }
    },

    /**
     * Remove a specific value from a filter's selection
     * Used when clicking filter chips to remove them
     * @param {string} filterKey - The internal filter identifier
     * @param {number} value - The data entry ID to remove
     */
    unsetFilterSelection(filterKey, value) {
      const filter = this.getFilter(filterKey);

      if (filter) {
        // Remove the value from the selected array
        filter.selected.splice(filter.selected.indexOf(value), 1);

        // Push the change to the history state
        this.replaceHistoryState();
      }
    },

    /**
     * preventSelectionWhileLoading
     */
    preventSelectionWhileLoading() {
      this.$watch("loading", (loading) => {
        if (loading) {
          this.filters.forEach(({ el }) => {
            if (el) {
              el.disabled = true;
            }
          });
        } else {
          this.filters.forEach(({ el }) => {
            // Keep track of the disabled state using a data attrib
            // So that we don't re-enable filters that were disabled by null results
            if (el && !el.dataset.disabled) {
              el.disabled = false;
            }
          });
        }
      });
    },

    /**
     * Perform fuzzy search on a single value
     * @param {string} searchQuery - The search query
     * @param {string} value - The value to search in
     * @returns {boolean} True if the value matches the search query
     */
    fuzzyMatch(searchQuery, value) {
      if (!searchQuery || !value) return false;

      // Convert to lowercase for case-insensitive search
      const query = searchQuery.toLowerCase().trim();
      const text = String(value).toLowerCase();

      // Split query into words for token-based matching
      const queryTokens = query.split(/\s+/);

      // Check if all query tokens are found in the text
      return queryTokens.every(token => text.includes(token));
    },

    /**
     * Search an entry across configured searchKeys
     * @param {object} entry - The entry to search
     * @param {string} searchQuery - The search query
     * @returns {boolean} True if the entry matches the search query
     */
    searchEntry(entry, searchQuery) {
      if (!searchQuery || this.searchKeys.length === 0) return true;

      // Check each configured search key
      return this.searchKeys.some(key => {
        const value = entry[key];

        // Handle different value types
        if (typeof value === 'string') {
          return this.fuzzyMatch(searchQuery, value);
        } else if (Array.isArray(value)) {
          // For arrays, check if any element matches
          return value.some(item => this.fuzzyMatch(searchQuery, item));
        } else if (typeof value === 'object' && value !== null) {
          // For objects (like categories), check values
          return Object.values(value).some(val => this.fuzzyMatch(searchQuery, val));
        }

        return false;
      });
    },

    /**
     * Filter entries based on selected filter values and search query
     * Uses dataKey to access the correct property in each entry
     * Example: if dataKey is 'postCategories', checks entry.postCategories[selectedId]
     *
     * @param {string|null} ignore - dataKey to ignore (used for filter counts)
     * @returns {object[]} Filtered array of entries
     */
    filteredEntries(ignore = null) {
      let results = this.entries;

      // Apply search filter first
      if (this.debouncedSearchQuery && this.searchKeys.length > 0) {
        results = results.filter(entry => this.searchEntry(entry, this.debouncedSearchQuery));
      }

      // Then apply category/tag filters
      return this.filters.reduce((filteredData, filter) => {
        if (ignore === filter.dataKey) {
          return filteredData;
        }

        return filteredData.filter((entry) => {
          if (filter.selected.length === 0) {
            return true;
          }

          return filter.selected.some((selectedValue) => {
            return entry[filter.dataKey][selectedValue];
          });
        });
      }, results);
    },

    /**
     * Handle filter option click
     * @param {string} filterKey - The internal filter identifier
     * @param {number} value - The data entry ID that was clicked
     */
    handleFilterClick(filterKey, value) {
      const filter = this.getFilter(filterKey);

      if (filter) {
        if (filter.multipleSelect) {
          if (filter.selected.includes(value)) {
            filter.selected.splice(filter.selected.indexOf(value), 1);
          } else {
            filter.selected.push(value);
          }
        } else {
          filter.selected = [value];
        }

        this.loading = true;
        this.handleFilterChange();
      }
    },

    /**
     * Handle filter state changes
     * Resets pagination and updates URL
     */
    handleFilterChange() {
      this.page = 1;
      this.replaceHistoryState();
      this.loading = false;
    },

    /**
     * Get the count of entries for a specific filter option
     * Used to show "(5)" next to filter options
     *
     * @param {string} entryKey - The dataKey to check (same as filter.dataKey)
     * @param {number} value - The data entry ID to count
     * @param {string|null} ignore - dataKey to ignore when filtering
     * @returns {number} Number of entries that match this filter option
     */
    getFilterCount(entryKey, value, ignore = null) {
      return this.filteredEntries(ignore).filter(
        (entry) => entry[entryKey] && entry[entryKey][value]
      ).length;
    },

    /**
     * handleSortChange
     */
    handleSortChange() {
      if (this.sortBy === "asc") {
        this.entries.sort((a, b) => {
          return a.title.localeCompare(b.title);
        });
      }
      if (this.sortBy === "desc") {
        this.entries.sort((a, b) => {
          return b.title.localeCompare(a.title);
        });
      }
      this.page = 1;
      this.replaceHistoryState();
      this.loading = false;
    },

    /**
     * Check if a filter option has any matching entries
     * Used to disable/dim filter options with no results
     *
     * @param {string} option - The dataKey to check (e.g., 'postCategories')
     * @param {number} targetId - The data entry ID to check for
     * @param {string} omitCategory - dataKey to omit when filtering
     * @returns {boolean} True if any entries match this filter option
     */
    isOptionFound(option, targetId, omitCategory = "") {
      for (let entry of this.filteredEntries(omitCategory)) {
        if (Object.keys(entry[option]).includes(targetId.toString())) {
          return true;
        }
      }
      return false;
    },

    /**
     * Get entries for the current page
     * @returns {object[]} Array of entries for current page
     */
    get paginatedEntries() {
      // Calculate the start and end entries for the current page
      const start = (this.page - 1) * this.perPage;
      const end = start + this.perPage;

      // Return the entries for the current page
      return this.filteredEntries().slice(start, end);
    },

    /**
     * Get pagination structure for UI
     * @returns {number[]} Array of page numbers to display
     */
    get pagination() {
      // Calculate the number of pages
      const pages = this.totalPages;

      // Create an array of pages
      const pagination = Array.from({ length: pages }, (_, i) => i + 1);

      // If there are more than 2 pages, filter out the pages that are not within 2 of the current page
      if (pages > 2) {
        return pagination.filter(
          (page) =>
            page === 1 ||
            page === this.page ||
            page === pages ||
            (page >= this.page - 2 && page <= this.page + 2)
        );
      } else {
        // Otherwise, return all the pages
        return pagination;
      }
    },

    /**
     * Get total number of pages
     * @returns {number} Total pages based on filtered entries and perPage
     */
    get totalPages() {
      // Calculate the number of pages
      return Math.ceil(this.filteredEntries().length / this.perPage);
    },

    /**
     * Navigate to a specific page and scroll to results
     * @param {number} page - Page number to navigate to
     * @param {HTMLElement|string|null} offsetElement - Element whose height should be added as positive offset, or CSS selector string
     */
    goToPage(page, offsetElement = null) {
      // Set the current page
      this.page = page;

      // Scroll to results with optional offset
      const results = this.$refs.results;
      if (results) {
        let offset = 0;

        if (offsetElement) {
          let element = null;

          // If offsetElement is a string, treat it as a selector
          if (typeof offsetElement === 'string') {
            const foundElement = document.querySelector(offsetElement);
            if (foundElement instanceof HTMLElement) {
              element = foundElement;
            }
          } else if (offsetElement instanceof HTMLElement) {
            element = offsetElement;
          }

          // Calculate offset from element height
          if (element && element.offsetHeight) {
            offset = element.offsetHeight;
          }
        }

        // Calculate target position with offset
        const resultsRect = results.getBoundingClientRect();
        const targetY = window.scrollY + resultsRect.top - offset;

        // Scroll to the calculated position
        window.scrollTo({
          top: Math.max(0, targetY), // Ensure we don't scroll above the page
          behavior: "smooth",
        });
      }
    },

    /**
     * replaceHistoryState
     */
    replaceHistoryState() {
      const params = new URLSearchParams(window.location.search);

      this.filters.forEach((filter) => {
        if (filter.selected.length > 0) {
          params.set(filter.urlKey, filter.selected.join(","));
        } else {
          params.delete(filter.urlKey);
        }
      });

      if (this.page > 1) {
        params.set("page", this.page);
      } else {
        params.delete("page");
      }

      if (this.debouncedSearchQuery) {
        params.set("search", this.debouncedSearchQuery);
      } else {
        params.delete("search");
      }

      const queryString = params.toString();
      const newUrl = queryString
        ? `${window.location.pathname}?${queryString}`
        : window.location.pathname;

      window.history.replaceState({}, "", newUrl);
    },

    /**
     * Reset a filter
     */
    resetFilter(filterKey) {
      const filter = this.getFilter(filterKey);

      if (filter) {
        filter.selected = [];
        this.loading = true;
        this.handleFilterChange();
      }
    },

    /**
     * Resets all filter values to empty arrays.
     */
    resetFilters() {
      this.filters.forEach((filter) => {
        filter.selected = [];
      });

      this.loading = true;
      this.handleFilterChange();
    },

    /**
     * Clear the search query
     */
    clearSearch() {
      this.searchQuery = "";
      this.debouncedSearchQuery = "";
    },

    /**
     * Reset all filters and search
     */
    resetAll() {
      this.resetFilters();
      this.clearSearch();
    },
  };
}
